/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Trans } from 'react-i18next';
import QuickLogin from '../pages/QuickLogin';
import LanguageSwitcher from '../widgets/LanguageSwitcher';
import { assetUrl, brandName, decorUrl, heroUrl } from '../../portalSettings';

/**
 * מסך ההתחברות בפריסה הסלולרית - שם משתמש וסיסמה יחד, לצד התמונה הגדולה.
 *
 * **ציור בלבד**, בדיוק כמו `ClassicLoginLayout`: מה שהמסך עושה מגיע ב-`view`
 * מ-`Login`, והפריסה אינה יודעת דבר על השרת.
 *
 * זו הפריסה היחידה שאינה מצוירת בתוך `Frame`, ולכן היא נושאת בעצמה את הלוגו, את
 * התמונה, את הקישוט ואת בורר השפה - שלושת אלה מגיעים מ-`Frame`/`Header` בכל מסך
 * אחר. זה גם ההבדל האמיתי היחיד בין שתי הערכות שאינו ניתן לביטוי ב-CSS בלבד: שאר
 * מסכי הזרימה חולקים DOM אחד ומקבלים עור (`stylesSim.css`, הבלוק של
 * `data-portal-skeleton="cellular"`).
 */
const CellularLoginLayout = ({ view }) => {
    const {
        t, reseller, state,
        showRegister, showRestoreUsername, showRestorePassword,
        quickMode, setQuickMode, quickLoginAvailable, quickCaptcha,
        captcha, submit, navigate,
        handleChange, handleSubmit, toggleShowPassword,
    } = view;

    /**
     * הלוגו של הריסיילר.
     *
     * `assetUrl` נותן שלוש שכבות - נכס שהועלה בבילינג, התיקייה הידנית, ואז כלום.
     * `null` פירושו שאין לוגו, ואז התג אינו מצויר בכלל: תג `img` בלי `src` הוא
     * ריבוע שבור על מסך ההתחברות.
     */
    const logo = assetUrl(reseller, 'logo', 'logo_new.png');

    /**
     * התמונה הגדולה. הנכס שהועלה, אחריו התמונה של הערכה, ואחריו כלום - ראה `heroUrl`.
     */
    const hero = heroUrl(reseller);

    /** הקישוט בתחתית. של המיתוג, ולא של סים קהילות בכל מסך - ראה `decorUrl`. */
    const decor = decorUrl(reseller);

    /**
     * העמודות והנכסים - זהים בשני המצבים, ולכן הם מוגדרים פעם אחת.
     *
     * מה שנבדל בין המהיר לסיסמה הוא תוכן העמודה הימנית בלבד, והוא נמסר פנימה.
     */
    const shell = (children) => (
        <div>
            <div className='sim-main-body'>
                {children}
                <div className='sim-mid-main'>
                    {hero&&<img src={hero} className='sim-content-logo' alt=''/>}
                </div>
                <div className='sim-left-main'>
                </div>
                {/* הסמל מוביל להתחלה, כמו ב-`Header` של שאר המסכים. המיקום עבר
                    לקישור: תמונה שממוקמת `fixed` בתוך קישור שאינו ממוקם משאירה את
                    שטח הלחיצה במקום אחר על המסך. */}
                {logo&&<Link to="/" className='sim-logo-link' aria-label={brandName(reseller)||''}><img className='sim-logo' src={logo} alt={brandName(reseller)||''}/></Link>}
                {decor&&<img src={decor} className='sim-people' alt=''/>}
                {/* בורר השפה - הפריסה הזו אינה מצוירת בתוך `Frame`, ולכן אין לה
                    `Header`, ובלעדיו לא הייתה בה שום דרך לשנות שפה. */}
                <LanguageSwitcher className='sim-language-switcher' />
            </div>
        </div>
    );

    if (quickMode) {
        return shell(
            <div className='sim-right-main-form'>
                <div className='sim-right-main'>
                    <span className='sim-h1'>{t("Welcome")}</span>
                    {/* כותרת המשנה הענקית (45 פיקסלים) יורדת במסך הזה:
                        מתחתיה יושב טופס שלם, ולא רק שני שדות. */}
                    <span className='sim-h2 sim-h2-small mt10'>{t("To the cellular subscribers management system")}</span>
                    <QuickLogin
                        resellerData={reseller}
                        captcha={quickCaptcha}
                        onUsePassword={() => setQuickMode(false)}
                        className='quick-login-sim'
                    />
                </div>
            </div>
        );
    }

    return shell(
        <form className='sim-right-main-form' onSubmit={(e) => handleSubmit(e)}>
            <div className='sim-right-main'>
                <span className='sim-h1'>{t("Welcome")}</span>
                <span className='sim-h2 mt10'>{t("To the cellular subscribers management system")}</span>
                <div className='input sim-input mt50' style={{display:'flex'}}><input className={state.errors.username?"error":""} type="text" id="username" name="username" autoComplete="username" placeholder={t("Please insert the username")} value={state.username} onChange={(e) => handleChange(e)} /></div>
                <div className='input sim-input' style={{display:'flex'}}> <input className={state.errors.password?"error":""} type="password" id="password" name="password" autoComplete="current-password" placeholder={t("Password")}  onChange={(e) => handleChange(e)} /><img src='/images/show-password-icon.png' className='input-icon ml5' onClick={(e) => toggleShowPassword(e)} alt=''/></div>

                {captcha.node}
                <button className='sim-button mt10' type="submit" disabled={submit.busy} aria-busy={submit.busy ? "true" : undefined}>{submit.busy ? t('Signing you in...') : t('Login')}{submit.busy && <span className='button-spinner' />}</button>

                {/* השגיאות - בשורה, כמו בפריסה המלאה, ולא בחלון קופץ.
                    מסלול שגיאה אחד לשתי הערכות; העור נותן לו את הצבע. */}
                {Object.entries(state.errors).map(([key, msg]) => (
                    <span key={key} className='sim-error mt10'><Trans i18nKey={msg} /></span>
                ))}

                {/* `type="button"` ולא `span` עם `onClick`: אלה קישורים בתוך
                    הטופס, ובלי הסוג הזה כל לחיצה עליהם הייתה שולחת אותו. */}
                {showRestoreUsername&&<button type='button' className='sim-link mt20' onClick={() => navigate('/restore-username')}><Trans i18nKey="Did you forget your username? <strong>Press here to restore it</strong>" /></button>}
                {showRestorePassword&&<button type='button' className='sim-link mt10' onClick={() => navigate('/reset-password')}><Trans i18nKey="Forgot your password? <strong>Press here to reset it</strong>" /></button>}
                {/* ההרשמה - גם כאן. עד היום היא הוצעה בהתחברות המהירה בלבד, ומי
                    שהגיע למסך הסיסמה בלי חשבון לא ראה שום דרך להירשם. */}
                {showRegister&&<button type='button' className='sim-link mt10' onClick={() => navigate('/register')}><Trans i18nKey="Don't have an account yet? <strong>Register now</strong>" /></button>}
                {/* הדרך חזרה למהיר. `type="button"` ולא `span`: זה קישור
                    בתוך טופס, ובלי הסוג הזה לחיצה עליו הייתה שולחת אותו. */}
                {quickLoginAvailable&&<button type='button' className='sim-link mt10' onClick={() => setQuickMode(true)}>{t("Quick login")}</button>}
            </div>
        </form>
    );
};

export default CellularLoginLayout;
